/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {FormattedMessage, useIntl} from 'react-intl'
import {normalizeSetBundleProduct} from '@salesforce/retail-react-app/app/utils/product-utils'
// Components
import {Box, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useProduct,
    useProducts,
    useCategory,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useVariant} from '@salesforce/retail-react-app/app/hooks'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useTracking from '@consteed/frontend/hooks/use-tracking'
// Project Components
import RecommendedProducts from '@salesforce/retail-react-app/app/components/recommended-products'
import ProductView from '@consteed/overrides/components/product-view'
import InformationAccordion from '@consteed/overrides/pages/product-detail/partials/information-accordion'
import ProductDetailPageDesignerPage from '@consteed/frontend/components/pageDesigner/ProductDetailPageDesignerPage'

import PageNotFound from '@consteed/overrides/pages/page-not-found'

// constant
import {
    API_ERROR_MESSAGE,
    EINSTEIN_RECOMMENDERS,
    MAX_CACHE_AGE
} from '@consteed/overrides/constants'
import {rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

import {
    SELECTED_ROLE_KEY,
    ROLE_RIDER,
    ROLE_VET,
    PRODUCT_IDS_RIDER,
    PRODUCT_IDS_VET,
    CONSTEED_MASTER_PRODUCT_ID
} from '@consteed/overrides/constants'

const ProductDetail = () => {
    const {formatMessage} = useIntl()
    const history = useHistory()
    const location = useLocation()
    const einstein = useEinstein()
    const {eventTracking, calculateNetPrice} = useTracking()
    const activeData = useActiveData()
    const {locale} = useMultiSite()
    /****************************** Basket *********************************/
    const {data: basket} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {res} = useServerContext()
    if (res) {
        res.set('Cache-Control', `s-maxage=${MAX_CACHE_AGE}`)
    }
    const isBasketLoading = !basket?.basketId

    /*************************** Product Detail and Category ********************/
    const {productId} = useParams()
    const urlParams = new URLSearchParams(location.search)
    const {
        data: product,
        isLoading: isProductLoading,
        isError: isProductError,
        error: productError
    } = useProduct(
        {
            parameters: {
                id: urlParams.get('pid') || productId,
                allImages: true
            }
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: product?.primaryCategoryId,
            level: 1
        }
    })

    /****************************** Sets and Bundles *********************************/
    const [childProductSelection, setChildProductSelection] = useState({})
    const [childProductOrderability, setChildProductOrderability] = useState({})
    const [selectedBundleQuantity, setSelectedBundleQuantity] = useState(1)
    const childProductRefs = React.useRef({})
    const isProductASet = product?.type.set
    const isProductABundle = product?.type.bundle

    let bundleChildVariantIds = ''
    if (isProductABundle)
        bundleChildVariantIds = Object.keys(childProductSelection)
            ?.map((key) => childProductSelection[key].variant.productId)
            .join(',')

    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory,master))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true
        }
    )

    if (isProductABundle && bundleChildrenData) {
        // Loop through the bundle children and update the inventory for variant selection
        product.bundledProducts.forEach(({product: childProduct}, index) => {
            const matchingChildProduct = bundleChildrenData.data.find(
                (bundleChild) => bundleChild.master.masterId === childProduct.id
            )
            if (matchingChildProduct) {
                product.bundledProducts[index].product = {
                    ...childProduct,
                    inventory: matchingChildProduct.inventory
                }
            }
        })
    }

    const comboProduct = isProductASet || isProductABundle ? normalizeSetBundleProduct(product) : {}

    const [primaryCategory, setPrimaryCategory] = useState(category)
    const variant = useVariant(product)
    // This page uses the `primaryCategoryId` to retrieve the category data. This attribute
    // is only available on `master` products. Since a variation will be loaded once all the
    // attributes are selected (to get the correct inventory values), the category information
    // is overridden. This will allow us to keep the initial category around until a different
    // master product is loaded.
    useEffect(() => {
        if (category) {
            setPrimaryCategory(category)
        }
    }, [category])

    /**************** Product Variant ****************/
    useEffect(() => {
        if (!variant) {
            return
        }
        // update the variation attributes parameter on
        // the url accordingly as the variant changes
        const updatedUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
            pid: variant?.productId
        })
        history.replace(updatedUrl)
    }, [variant])

    /**************** Add To Cart ****************/
    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    const handleAddToCart = async (productSelectionValues) => {
        try {
            console.log(productSelectionValues)
            const productItems = productSelectionValues.map(({variant, quantity}) => ({
                productId: variant.productId,
                price: variant.price,
                quantity
            }))

            await addItemToNewOrExistingBasket(productItems)

            einstein.sendAddToCart(productItems)
            // we do not have tax information on the product -> net price is calulated with locale tax rate
            try {
                eventTracking('add_to_cart', {
                    currency: productSelectionValues[0].product.currency,
                    value: calculateNetPrice({
                        price: productItems.reduce(
                            (acc, item) => acc + item.price * item.quantity,
                            0
                        ),
                        taxRate: locale?.taxRate || 0
                    }),
                    items: productItems.map((item, index) => ({
                        item_id: item.productId,
                        item_name: productSelectionValues[index].product.name,
                        price: calculateNetPrice({
                            price: item.price,
                            taxRate: locale?.taxRate || 0
                        }),
                        quantity: item.quantity
                    }))
                })
            } catch (error) {
                // catch tracking errors to make sure the app works even if tracking fails
            }

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return productSelectionValues
        } catch (error) {
            showError(error)
        }
    }

    /**************** Product Set/Bundles Handlers ****************/
    const handleChildProductValidation = useCallback(() => {
        // Run validation for all child products. This will ensure the error
        // messages are shown.
        Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
            validateOrderability({scrollErrorIntoView: false})
        })

        // Using ot state for which child products are selected, scroll to the first
        // one that isn't selected.
        const selectedProductIds = Object.keys(childProductSelection)
        const firstUnselectedProduct = comboProduct.childProducts.find(
            ({product: childProduct}) => !selectedProductIds.includes(childProduct.id)
        )?.product

        if (firstUnselectedProduct) {
            // Get the reference to the product view and scroll to it.
            const {ref} = childProductRefs.current[firstUnselectedProduct.id]

            if (ref.scrollIntoView) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                })
            }

            return false
        }

        return true
    }, [product, childProductSelection])

    /**************** Product Set Handlers ****************/
    const handleProductSetAddToCart = () => {
        // Get all the selected products, and pass them to the addToCart handler which
        // accepts an array.
        const productSelectionValues = Object.values(childProductSelection)
        return handleAddToCart(productSelectionValues)
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (product && product.type.set) {
            einstein.sendViewProduct(product)
            const childrenProducts = product.setProducts
            childrenProducts.map((child) => {
                try {
                    einstein.sendViewProduct(child)
                } catch (err) {
                    console.error(err)
                }
                activeData.sendViewProduct(category, child, 'detail')
            })
        } else if (product) {
            try {
                einstein.sendViewProduct(product)
            } catch (err) {
                console.error(err)
            }
            activeData.sendViewProduct(category, product, 'detail')
        }
    }, [product])

    useEffect(() => {
        if (variant) {
            return
        }

        if (product && product.id === CONSTEED_MASTER_PRODUCT_ID) {
            // TODO: Change this to `useLocalStorage` hook when localStorage detection is more robust
            const selectedRole = window.localStorage.getItem(SELECTED_ROLE_KEY)
            let roleProductId
            switch (selectedRole) {
                case ROLE_RIDER:
                    roleProductId = PRODUCT_IDS_RIDER?.[0]
                    break
                case ROLE_VET:
                    roleProductId = PRODUCT_IDS_VET?.[0]
                    break
                default:
                // no role selected
            }

            if (roleProductId) {
                // update the variation attributes parameter on
                // the url accordingly to the role selected
                const updatedUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
                    pid: roleProductId
                })
                history.replace(updatedUrl)
            }
        }
    }, [product, variant])
    const containerStyle = {
        backgroundImage: {
            base: 'none',
            xl: `url(${getAssetUrl('static/img/global/bg_pattern.png')})`
        },
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 600px'
    }
    /**************** Error Handling ****************/
    if (isProductError || isCategoryError) {
        console.error(isProductError ? productError : categoryError)
        return <PageNotFound />
    }
    return (
        <Box {...containerStyle}>
            <Box
                className="sf-product-detail-page"
                layerStyle="page"
                data-testid="product-details-page"
            >
                <Helmet>
                    <title>{product?.pageTitle}</title>
                    <meta name="description" content={product?.pageDescription} />
                </Helmet>

                <Stack spacing={[8, 16]}>
                    {isProductASet ? (
                        <Fragment>
                            {/* Product Set: parent product */}
                            <ProductView
                                product={product}
                                category={primaryCategory?.parentCategoryTree || []}
                                addToCart={handleProductSetAddToCart}
                                isProductLoading={isProductLoading}
                                isBasketLoading={isBasketLoading}
                                validateOrderability={handleChildProductValidation}
                                childProductOrderability={childProductOrderability}
                                setSelectedBundleQuantity={setSelectedBundleQuantity}
                            />

                            <hr />

                            {/* TODO: consider `childProduct.belongsToSet` */}
                            {
                                // Render the child products
                                comboProduct.childProducts.map(
                                    ({product: childProduct, quantity: childQuantity}) => (
                                        <Box key={childProduct.id} data-testid="child-product">
                                            <ProductView
                                                // Do not use an arrow function as we are manipulating the functions scope.
                                                ref={function (ref) {
                                                    // Assign the "set" scope of the ref, this is how we access the internal
                                                    // validation.
                                                    childProductRefs.current[childProduct.id] = {
                                                        ref,
                                                        validateOrderability:
                                                            this.validateOrderability
                                                    }
                                                }}
                                                product={childProduct}
                                                isProductPartOfSet={isProductASet}
                                                isProductPartOfBundle={isProductABundle}
                                                childOfBundleQuantity={childQuantity}
                                                selectedBundleParentQuantity={
                                                    selectedBundleQuantity
                                                }
                                                addToCart={
                                                    isProductASet
                                                        ? (variant, quantity) =>
                                                              handleAddToCart([
                                                                  {
                                                                      product: childProduct,
                                                                      variant,
                                                                      quantity
                                                                  }
                                                              ])
                                                        : null
                                                }
                                                onVariantSelected={(product, variant, quantity) => {
                                                    if (quantity) {
                                                        setChildProductSelection(
                                                            (previousState) => ({
                                                                ...previousState,
                                                                [product.id]: {
                                                                    product,
                                                                    variant,
                                                                    quantity: isProductABundle
                                                                        ? childQuantity
                                                                        : quantity
                                                                }
                                                            })
                                                        )
                                                    } else {
                                                        const selections = {
                                                            ...childProductSelection
                                                        }
                                                        delete selections[product.id]
                                                        setChildProductSelection(selections)
                                                    }
                                                }}
                                                isProductLoading={isProductLoading}
                                                isBasketLoading={isBasketLoading}
                                                setChildProductOrderability={
                                                    setChildProductOrderability
                                                }
                                            />
                                            <InformationAccordion product={childProduct} />

                                            <Box display={['none', 'none', 'none', 'block']}>
                                                <hr />
                                            </Box>
                                        </Box>
                                    )
                                )
                            }
                        </Fragment>
                    ) : (
                        <Fragment>
                            <ProductView
                                product={product}
                                category={primaryCategory?.parentCategoryTree || []}
                                addToCart={(variant, quantity) =>
                                    handleAddToCart([{product, variant, quantity}])
                                }
                                isProductLoading={isProductLoading}
                                isBasketLoading={isBasketLoading}
                            />
                            <InformationAccordion product={product} />
                            <ProductDetailPageDesignerPage product={product} />
                        </Fragment>
                    )}

                    {/* Product Recommendations */}
                    <Stack spacing={16}>
                        {!isProductASet && (
                            <RecommendedProducts
                                title={
                                    <FormattedMessage
                                        defaultMessage="Complete the Set"
                                        id="product_detail.recommended_products.title.complete_set"
                                    />
                                }
                                recommender={EINSTEIN_RECOMMENDERS.PDP_COMPLETE_SET}
                                products={[product]}
                                mx={{base: -4, md: -8, lg: 0}}
                                shouldFetch={() => product?.id}
                            />
                        )}
                        <RecommendedProducts
                            title={
                                <FormattedMessage
                                    defaultMessage="You might also like"
                                    id="product_detail.recommended_products.title.might_also_like"
                                />
                            }
                            recommender={EINSTEIN_RECOMMENDERS.PDP_MIGHT_ALSO_LIKE}
                            products={[product]}
                            mx={{base: -4, md: -8, lg: 0}}
                            shouldFetch={() => product?.id}
                        />

                        <RecommendedProducts
                            // The Recently Viewed recommender doesn't use `products`, so instead we
                            // provide a key to update the recommendations on navigation.
                            key={location.key}
                            title={
                                <FormattedMessage
                                    defaultMessage="Recently Viewed"
                                    id="product_detail.recommended_products.title.recently_viewed"
                                />
                            }
                            recommender={EINSTEIN_RECOMMENDERS.PDP_RECENTLY_VIEWED}
                            mx={{base: -4, md: -8, lg: 0}}
                        />
                    </Stack>
                </Stack>
            </Box>
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object
}

export default ProductDetail
